import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import styled from 'styled-components'
import { Tooltip } from 'antd'
import { CSVLink } from 'react-csv'

import { colours, sizes } from '@qflow/theme'

import {
  ColourButton,
  icons,
  Label,
  NavButtonDecorator,
  Spacer
} from 'portal/lib/primitives'
import { trackMixpanel } from 'portal/store/mixpanel/actions'

export const ProjectConnectionsSummaryTable = ({
  summary
}: ProjectConnectionsSummaryTableProps) => {
  const [copiedText, setCopiedText] = useState('')
  const dispatch = useDispatch()
  const { accountId } = useParams<{
    accountId: string
  }>()

  const projectPhases = {
    1: 'Demolition',
    2: 'Excavation',
    3: 'Construction',
    4: 'Post-Construction',
    5: 'Modular',
    6: 'Post-Completion'
  }

  const calculateData = () => {
    const headerRow = [
      'Project ID',
      'Project Name',
      'Delivery Email',
      'Waste Email',
      'Autodesk',
      'Procore',
      'One Click LCA',
      'EC3',
      'SmartWaste Default Project ID',
      'SW Project Phase',
      'SW Waste Enabled',
      'SW Delivery Enabled',
      'SW Email Addresses'
    ]

    const csvData = [
      summary.projectId || '',
      summary.projectName || '',
      summary.provisioningEmailsDetails
        ? summary.provisioningEmailsDetails
            .filter(email => email.module === 'Delivery')
            .map(email => email.email)
            .join(', ')
        : '',
      summary.provisioningEmailsDetails
        ? summary.provisioningEmailsDetails
            .filter(email => email.module === 'Waste')
            .map(email => email.email)
            .join(', ')
        : '',
      summary.isAutodeskEnabledAndConfigured ? 'Yes' : 'No',
      summary.isProcoreEnabled ? 'Yes' : 'No',
      summary.isOneClickLcaEnabled ? 'Yes' : 'No',
      summary.isEC3Enabled ? 'Yes' : 'No',
      summary.smartWasteDetails?.defaultSWProjectId || '',
      summary.smartWasteDetails?.defaultProjectPhase
        ? projectPhases[summary.smartWasteDetails.defaultProjectPhase]
        : '',
      summary.smartWasteDetails?.isWasteEnabled ? 'Yes' : 'No',
      summary.smartWasteDetails?.isDeliveryEnabled ? 'Yes' : 'No',
      summary.smartWasteDetails?.emailConfigDetails
        ?.map(
          emailConfig =>
            `${emailConfig.email} (${emailConfig.swProjectId} ${projectPhases[emailConfig.projectPhase]})`
        )
        .join(', ') || ''
    ]

    return [headerRow, csvData]
  }

  // Commented out until API key details are available/filtered
  // const getPermissions = (apiKey: publicApiKeyDetails) => {
  //   const permissions: string[] = []

  //   if (apiKey.canRead) {
  //     permissions.push('Read')
  //   }
  //   if (apiKey.canWrite) {
  //     permissions.push('Write')
  //   }
  //   return permissions.join(', ')
  // }

  const projectCloseDownUrl = useMemo(() => {
    const queryParams = new URLSearchParams({
      text9: summary.projectName
    })
    return `https://forms.monday.com/forms/47cd73b9e19475ec061771dc08959167?${queryParams.toString()}`
  }, [summary])

  return (
    <ProjectsData>
      <TitleBar>
        <h3>{summary.projectName} Data</h3>
        <ButtonWrapper>
          <StyledCSVLink
            onClick={() => {
              dispatch(
                trackMixpanel('frontend_individual_project_data_export', {
                  account_id: accountId,
                  project_id: summary.projectId
                })
              )
            }}
            filename={`${summary.projectName}-data.csv`}
            data={calculateData()}
          >
            <icons.Download size={sizes.SMALL} colour={'currentColor'} />
            <Spacer.Fine />
            Export Project Data
          </StyledCSVLink>
          <NavButtonDecorator to={projectCloseDownUrl}>
            <ColourButton.Trigger>
              <icons.NewTabIcon colour="currentColor" />
              <Spacer.Fine />
              <Label isChild colour="inherit">
                Request Project Closeout
              </Label>
            </ColourButton.Trigger>
          </NavButtonDecorator>
        </ButtonWrapper>
      </TitleBar>
      <Section>
        <h4>Email</h4>
        <DataWrapper>
          {summary.provisioningEmailsDetails?.map((email, index) => (
            <DataPanel key={index}>
              <DataPanelTitle>{email.module} Email</DataPanelTitle>
              <DataPanelDetails>
                <div>{email.email}</div>
                <div
                  style={{
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(email.email)
                    setCopiedText(email.email)
                    setTimeout(() => {
                      setCopiedText('')
                    }, 2000)
                  }}
                >
                  <Tooltip title="Copy to clipboard">
                    <icons.CopyToClipbaord id={`email-copy-${index}`} />
                  </Tooltip>
                </div>
                {copiedText === email.email && (
                  <CopiedText>
                    <icons.CircleTicked size={sizes.SMALL} />
                    <div>Copied</div>
                  </CopiedText>
                )}
              </DataPanelDetails>
            </DataPanel>
          ))}
        </DataWrapper>
      </Section>
      {/* <Section>
        <h4>Public API</h4>
        <ApiTitleRow>
          <div>Name</div>
          <div>Permissions</div>
        </ApiTitleRow>
        {summary.publicAPIKeys?.map((apiKey, index) => (
          <ApiRow
            key={index}
            style={
              index === summary.publicAPIKeys.length - 1
                ? { borderBottom: 'none', marginBottom: '0' }
                : {}
            }
          >
            <DataPanelTitle>{apiKey.apiKeyName}</DataPanelTitle>
            <DataPanelDetails>
              <div>{getPermissions(apiKey)}</div>
            </DataPanelDetails>
          </ApiRow>
        ))}
      </Section> */}
      {/* <Section>
        <h4>Mobile</h4>
      </Section> */}
      <Section>
        <h4>Integrations Summary</h4>
        <Integration className="Integration">
          <Name>Autodesk</Name>
          {summary.isAutodeskEnabledAndConfigured ? (
            <StatusActive>
              <icons.CircleTicked colour={'currentColor'} />
              Active
            </StatusActive>
          ) : (
            <StatusInactive>
              <icons.CircleCrossed
                colour={'currentColor'}
                size={sizes.MEDIUM}
              />
              Inactive
            </StatusInactive>
          )}
        </Integration>
        <Integration className="Integration">
          <Name>EC3</Name>
          {summary.isEC3Enabled ? (
            <StatusActive>
              <icons.CircleTicked colour={'currentColor'} />
              Active
            </StatusActive>
          ) : (
            <StatusInactive>
              <icons.CircleCrossed
                colour={'currentColor'}
                size={sizes.MEDIUM}
              />
              Inactive
            </StatusInactive>
          )}
        </Integration>
        <Integration className="Integration">
          <Name>One Click LCA</Name>
          {summary.isOneClickLcaEnabled ? (
            <StatusActive>
              <icons.CircleTicked colour={'currentColor'} />
              Active
            </StatusActive>
          ) : (
            <StatusInactive>
              <icons.CircleCrossed
                colour={'currentColor'}
                size={sizes.MEDIUM}
              />
              Inactive
            </StatusInactive>
          )}
        </Integration>
        <Integration className="Integration">
          <Name>Procore</Name>
          {summary.isProcoreEnabled ? (
            <StatusActive>
              <icons.CircleTicked colour={'currentColor'} />
              Active
            </StatusActive>
          ) : (
            <StatusInactive>
              <icons.CircleCrossed
                colour={'currentColor'}
                size={sizes.MEDIUM}
              />
              Inactive
            </StatusInactive>
          )}
        </Integration>
        <Integration className="Integration" style={{ borderBottom: 'none' }}>
          <Name>SmartWaste</Name>
          {summary.smartWasteDetails ? (
            <StatusActive>
              <icons.CircleTicked colour={'currentColor'} />
              Active
            </StatusActive>
          ) : (
            <StatusInactive>
              <icons.CircleCrossed
                colour={'currentColor'}
                size={sizes.MEDIUM}
              />
              Inactive
            </StatusInactive>
          )}
        </Integration>
        {summary.smartWasteDetails ? (
          <>
            <DataWrapper style={{ padding: '0 0 0.625rem 0' }}>
              <DataPanel>
                <DataPanelTitle>Default Project ID</DataPanelTitle>
                <DataPanelDetails>
                  <div>{summary.smartWasteDetails.defaultSWProjectId}</div>
                </DataPanelDetails>
              </DataPanel>
              <DataPanel>
                <DataPanelTitle>Default Project Phase</DataPanelTitle>
                <DataPanelDetails>
                  <div>
                    {
                      projectPhases[
                        summary.smartWasteDetails.defaultProjectPhase
                      ]
                    }
                  </div>
                </DataPanelDetails>
              </DataPanel>
              <DataPanel>
                <DataPanelTitle>Delivery Enabled</DataPanelTitle>
                <DataPanelDetails>
                  <div>
                    {summary.smartWasteDetails.isDeliveryEnabled ? 'Yes' : 'No'}
                  </div>
                </DataPanelDetails>
              </DataPanel>
              <DataPanel>
                <DataPanelTitle>Waste Enabled</DataPanelTitle>
                <DataPanelDetails>
                  <div>
                    {summary.smartWasteDetails.isWasteEnabled ? 'Yes' : 'No'}
                  </div>
                </DataPanelDetails>
              </DataPanel>
            </DataWrapper>
            {summary.smartWasteDetails.emailConfigDetails?.length > 0 && (
              <SmartWasteDetails>
                {summary.smartWasteDetails.emailConfigDetails.map(
                  (emailConfigDetails, index) => (
                    <details key={`sw-${index}`} open>
                      <summary>
                        <ChevronWrapper>
                          <icons.ChevronDown size={'8'} />
                        </ChevronWrapper>
                        SmartWaste Entry {index + 1}
                      </summary>
                      <DataWrapper key={index}>
                        <DataPanel>
                          <DataPanelTitle>Email Address</DataPanelTitle>
                          <DataPanelDetails>
                            <div>{emailConfigDetails.email}</div>
                            <div
                              style={{
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  emailConfigDetails.email
                                )
                                setCopiedText(emailConfigDetails.email)
                                setTimeout(() => {
                                  setCopiedText('')
                                }, 2000)
                              }}
                            >
                              <Tooltip title="Copy to clipboard">
                                <icons.CopyToClipbaord
                                  id={`sw-email-copy-${index}`}
                                />
                              </Tooltip>
                            </div>
                            {copiedText === emailConfigDetails.email && (
                              <CopiedText>
                                <icons.CircleTicked size={sizes.SMALL} />
                                <div>Copied</div>
                              </CopiedText>
                            )}
                          </DataPanelDetails>
                        </DataPanel>
                        <DataPanel>
                          <DataPanelTitle>Project ID</DataPanelTitle>
                          <DataPanelDetails>
                            <div>{emailConfigDetails.swProjectId}</div>
                          </DataPanelDetails>
                        </DataPanel>
                        <DataPanel>
                          <DataPanelTitle>Project Phase</DataPanelTitle>
                          <DataPanelDetails>
                            <div>
                              {projectPhases[emailConfigDetails.projectPhase]}
                            </div>
                          </DataPanelDetails>
                        </DataPanel>
                      </DataWrapper>
                    </details>
                  )
                )}
              </SmartWasteDetails>
            )}
          </>
        ) : null}
      </Section>
    </ProjectsData>
  )
}

type ProjectConnectionsSummaryTableProps = {
  summary: projectConnectionsSummary
}
export interface projectConnectionsSummary {
  projectName: string
  projectId: string
  isAlertsEnabled: boolean
  isAutodeskEnabledAndConfigured: boolean
  isProcoreEnabled: boolean
  isOneClickLcaEnabled: boolean
  isEC3Enabled: boolean
  provisioningEmailsDetails: provisioningEmailsDetails[]
  publicAPIKeys: publicApiKeyDetails[]
  smartWasteDetails: smartWasteDetails | null
}

export interface provisioningEmailsDetails {
  email: string
  module: string
}

export interface publicApiKeyDetails {
  apiKeyName: string
  canRead: boolean
  canWrite: boolean
}

export interface smartWasteDetails {
  defaultSWProjectId: string
  isDeliveryEnabled: boolean
  isWasteEnabled: boolean
  defaultProjectPhase: string
  emailConfigDetails: smartWasteEmailConfigDetails[]
}

export interface smartWasteEmailConfigDetails {
  email: string
  swProjectId: string
  projectPhase: string
}

const ProjectsData = styled.div`
  flex-grow: 1;
  background-color: ${colours.WHITE};
  color: ${colours.CORE_NAVY};
  border-radius: 0.625rem;
  padding: 1rem 1.5rem 1rem 1.5rem;
  box-shadow: 0px 1px 3px 0px #00000026;
`
const TitleBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.875rem;

  & > h3 {
    color: ${colours.CORE_NAVY};
    font-family: 'Jakarta Sans', 'Inter', sans-serif !important;
    font-size: 1.125rem;
    font-weight: 700;
    margin: 0;
  }
`
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
`
const StyledCSVLink = styled(CSVLink)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: ${colours.DARK_GREY};
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid ${colours.BORDER_GREY};
  border-radius: 5px;
  padding: 0.25rem 0.625rem;

  &:hover,
  &:focus,
  &:active {
    background-color: ${colours.CORE_NAVY};
    border-color: ${colours.CORE_NAVY};
    color: ${colours.WHITE};
  }
`

const Section = styled.div`
  background-color: ${colours.CONTROL_PANEL_GREY};
  border-radius: 0.625rem;
  padding: 1rem 1.5rem;
  margin-bottom: 0.625rem;

  & > h4 {
    color: ${colours.CORE_NAVY};
    font-family: 'Jakarta Sans', 'Inter', sans-serif !important;
    font-size: 0.9375rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
  }
`
const DataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.625rem;
  padding: 0.625rem 0;
`
const DataPanel = styled.div`
  width: 50%;
  padding: 0;
`
const DataPanelTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 400;
  color: ${colours.MID_GREY};
`
const DataPanelDetails = styled.div`
  font-size: 0.875rem;
  font-weight: 400;
  color: ${colours.MID_GREY};
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
`
const CopiedText = styled.div`
  font-size: 0.75rem;
  font-weight: 400;
  color: ${colours.GREEN};
  display: flex;
  align-items: center;
  gap: 0.125rem;

  & > div {
    line-height: 0.75rem;
  }
`
// Commented out until API key details are available/filtered
// const ApiTitleRow = styled.div`
//   display: flex;
//   justify-content: space-between;
//   align-items: center;
//   padding: 0.625rem 0 0 0;
//   gap: 0.625rem;

//   & > div {
//     font-size: 0.75rem;
//     font-weight: 400;
//     color: ${colours.MID_GREY};
//     width: 50%;
//   }
// `
// const ApiRow = styled.div`
//   display: flex;
//   justify-content: space-between;
//   align-items: center;
//   padding: 0.625rem 0;
//   gap: 0.625rem;
//   border-bottom: 1px solid ${colours.CLOUD_GREY};

//   & > div {
//     color: ${colours.MID_GREY};
//     font-size: 0.875rem;
//     font-weight: 400;
//     width: 50%;
//   }
// `
const Integration = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.625rem 0;
  border-bottom: 1px solid ${colours.CLOUD_GREY};
`
const Name = styled.div`
  color: ${colours.MID_GREY};
  font-size: 0.9375rem;
  font-weight: 500;
`
const StatusActive = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  font-weight: 400;
  color: ${colours.GREEN};
  width: 4.75rem;
`
const StatusInactive = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  font-weight: 400;
  color: ${colours.ERROR};
  width: 4.75rem;
`
const SmartWasteDetails = styled.div`
  padding: 0 0 0.5rem 0;

  > details {
    padding: 0.625rem 1.25rem;
    background-color: ${colours.PALE_BLUEY_GREY};
    border-radius: 0;
    margin-bottom: 0.125rem;

    cursor: pointer;
    list-style: none;
    outline: none;
    user-select: none;
    &::-webkit-details-marker {
      display: none;
    }

    &[open] {
      SUMMARY > DIV {
        background: ${colours.CORE_NAVY};
        color: ${colours.WHITE};
      }
      SUMMARY > DIV > SVG {
        transform: rotate(180deg) !important;
      }
    }

    & > summary {
      display: flex;
      align-items: center;
      padding: 0;
      gap: 0.625rem;
    }
  }
  > details:first-of-type {
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
  }
  > details:last-of-type {
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }
`
const ChevronWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  border: 1px solid ${colours.CORE_NAVY};
  border-radius: 100%;
`
